import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, NotFoundPage, PageContainer } from '@/shared/ui';

import {
  useBindPriceListStore,
  usePriceList,
  usePriceListBindings,
  usePriceListLines,
  usePriceListsOverview,
  useProduct,
  useProductPriceLines,
  useProducts,
  useRetailStores,
  useSearchProducts,
  useUnbindPriceListStore,
  useUpdatePriceList,
} from '../../hooks';
import { formatPrice } from '../../lib/format';
import { deriveTierColumns, effectiveTierAmount } from '../../lib/priceGrid';
import { distinctProductCount } from '../../lib/priceListCoverage';
import { governingBaseLine } from '../../lib/priceSlots';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { PRICE_LIST_KIND_LABELS } from '../../model/options';
import type { PriceListRequest, RetailStoreOption } from '../../model/priceListTypes';
import type { PriceLineResponse } from '../../model/types';
import { PriceCellEditorDrawer } from '../components/PriceCellEditorDrawer';
import { PriceListFormDialog } from '../components/PriceListFormDialog';

type LinesFilter = 'all' | 'scheduled';

function stateColor(state: string): 'success' | 'warning' | 'error' | 'default' {
  if (state === 'ACTIVE') return 'success';
  if (state === 'REQUIRES_APPROVAL') return 'warning';
  if (state === 'BLOCKED') return 'error';
  return 'default';
}

/** One row of the list's own lines table — resolves the product name + the base-slot amount via the SAME
 * per-product lines fetch the price grid already uses (`useProductPriceLines`), so this row
 * mirrors the grid's established per-row pattern rather than inventing a new one. */
function PriceListLineRow({
  line,
  tiers,
  canManage,
  onEdit,
}: {
  line: PriceLineResponse;
  tiers: number[];
  canManage: boolean;
  onEdit: (product: { id: string; name?: string }, lines: PriceLineResponse[]) => void;
}) {
  const { t } = useTranslation('catalog');
  const productQuery = useProduct(line.productId);
  const allLinesQuery = useProductPriceLines(line.productId);
  // R0 (design spec §8.0, plan-merchant-portal-018): the product's base price is the GOVERNING
  // base-slot line — all three discriminators null AND ACTIVE with a validity window covering
  // now. The old predicate tested only two of the three (a customer-GROUP line has no
  // `channelCode` and no `priceListCode`, so a VIP price rendered here as "Base") and took the
  // first array hit regardless of state or window (an EXPIRED/BLOCKED/future line could win).
  // `governingBaseLine` is the single source of truth — never re-derive the rule here.
  const baseLine = governingBaseLine(allLinesQuery.data ?? [], new Date());
  const baseAmount = baseLine ? effectiveTierAmount(baseLine, 1) : undefined;
  const productName = productQuery.data?.name ?? line.productId;

  const editButton = (
    <IconButton
      size="small"
      aria-label={t('priceListDetail.lines.editAria', { name: productName })}
      disabled={!canManage}
      onClick={() => onEdit({ id: line.productId, name: productName }, allLinesQuery.data ?? [line])}
    >
      <EditRoundedIcon fontSize="small" />
    </IconButton>
  );

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {productName}
        </Typography>
        {line.validFrom && new Date(line.validFrom) > new Date() ? (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            sx={{ mt: 0.5 }}
            label={t('priceListDetail.lines.scheduledChip', {
              date: new Intl.DateTimeFormat(undefined, { dateStyle: 'short' }).format(new Date(line.validFrom)),
            })}
          />
        ) : null}
      </TableCell>
      {tiers.map((tier) => (
        <TableCell key={tier} align="right">
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {(() => {
              const amount = effectiveTierAmount(line, tier);
              return amount !== undefined ? formatPrice(amount) : '—';
            })()}
          </Typography>
        </TableCell>
      ))}
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {baseAmount !== undefined ? formatPrice(baseAmount) : '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" label={line.state} color={stateColor(line.state)} />
      </TableCell>
      <TableCell align="right">
        {canManage ? editButton : (
          <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
            <span>{editButton}</span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

/** List detail — `/catalog/pricing/price-lists/:code` (design spec §8.3): bindings + STATIC-lines
 * authoring for one price list. */
export function PriceListDetailPage() {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const { code = '' } = useParams<{ code: string }>();
  const { canPricingView, canPricingManage, canPricingAdmin } = useCatalogPermissions();
  const { toast, notify, clear } = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [lifecycleConfirm, setLifecycleConfirm] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [bindPick, setBindPick] = useState<RetailStoreOption | null>(null);
  const [unbindTarget, setUnbindTarget] = useState<RetailStoreOption | null>(null);
  const [linesFilter, setLinesFilter] = useState<LinesFilter>('all');
  const [productSearch, setProductSearch] = useState('');
  const [addTarget, setAddTarget] = useState<{ id: string; name?: string } | null>(null);
  const [editTarget, setEditTarget] = useState<{ product: { id: string; name?: string }; lines: PriceLineResponse[] } | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const listQuery = usePriceList(code, { enabled: Boolean(code) });
  const bindingsQuery = usePriceListBindings(code, { enabled: Boolean(code) });
  const linesQuery = usePriceListLines({ priceListCode: code }, { enabled: Boolean(code) });
  const storesQuery = useRetailStores();
  const updateMutation = useUpdatePriceList();
  const bindMutation = useBindPriceListStore();
  const unbindMutation = useUnbindPriceListStore();
  const overview = usePriceListsOverview(); // cross-list bindings — "already bound elsewhere" hint
  const assortmentQuery = useProducts({});

  const searchQuery = useSearchProducts({ query: productSearch }, { enabled: productSearch.trim() !== '' });
  const addTargetLinesQuery = useProductPriceLines(addTarget?.id, { enabled: Boolean(addTarget) });

  if (!canPricingView) {
    return (
      <PageContainer title={t('priceListDetail.title')}>
        <ErrorState title={t('pricing.accessRestricted.title')} message={t('pricing.accessRestricted.message')} />
      </PageContainer>
    );
  }

  if (listQuery.isLoading) {
    return (
      <PageContainer title={t('priceListDetail.title')}>
        <LoadingState label={t('priceListDetail.loading')} />
      </PageContainer>
    );
  }

  if (listQuery.error?.status === 404) {
    return <NotFoundPage />;
  }

  if (listQuery.isError || !listQuery.data) {
    return (
      <PageContainer title={t('priceListDetail.title')}>
        <ErrorState message={listQuery.error?.message} onRetry={() => void listQuery.refetch()} />
      </PageContainer>
    );
  }

  const list = listQuery.data;
  const bindings = bindingsQuery.data ?? [];
  const stores = storesQuery.data ?? [];
  const storeByCode = new Map(stores.map((s) => [s.code, s] as const));
  const boundStores = bindings.map((b) => storeByCode.get(b.storeCode) ?? { code: b.storeCode, name: b.storeCode });

  // "already bound elsewhere" hint for the bind dialog's Autocomplete options (design spec §8.3).
  const elsewhereByStore = new Map<string, string>(); // storeCode -> other list's name
  for (const otherList of overview.lists) {
    if (otherList.code === code) continue;
    for (const binding of overview.bindingsByCode.get(otherList.code) ?? []) {
      elsewhereByStore.set(binding.storeCode, otherList.name);
    }
  }
  const bindableStores = stores.filter((s) => !bindings.some((b) => b.storeCode === s.code));

  const allLines = linesQuery.data ?? [];
  // Distinct products, not raw line rows (a product legitimately holds a current + a future-dated
  // scheduled line in this list at once) — see `distinctProductCount`'s doc comment.
  const linesCount = linesQuery.data ? distinctProductCount(linesQuery.data) : null;
  const assortmentTotal = assortmentQuery.data?.totalElements ?? null;
  const coveragePercent =
    linesCount !== null && assortmentTotal
      ? Math.min(100, Math.round((linesCount / assortmentTotal) * 100))
      : null;
  const now = new Date();
  const scheduled = allLines.filter((l) => l.state === 'ACTIVE' && l.validFrom && new Date(l.validFrom) > now);
  const nextChangeAt = scheduled.map((l) => l.validFrom as string).sort()[0] ?? null;

  // The product-search Autocomplete above the table is for PICKING a product to add (design
  // spec §8.3's "Add product") — the underlying `GET .../price-lines` has no server-side
  // name filter for list lines, so this table itself is only filtered by `linesFilter`.
  const filteredLines =
    linesFilter === 'scheduled'
      ? allLines.filter((l) => l.validFrom && new Date(l.validFrom) > now)
      : allLines;
  const pageLines = filteredLines.slice(page * size, page * size + size);
  // Data-driven tier columns (design spec R5 fix, WP4.3) — derived from the CURRENT PAGE's own
  // lines, replacing the hardcoded [1, 10, 100].
  const tiers = deriveTierColumns(pageLines);

  const handleSaveEdit = (body: PriceListRequest) => {
    updateMutation.mutate(
      { code: list.code, body: { ...body, active: list.active } },
      {
        onSuccess: () => {
          setEditOpen(false);
          notify('success', t('priceListsPanel.editTitle'));
        },
        onError: (error) => notify('error', error.message),
      },
    );
  };

  const confirmLifecycle = () => {
    const nextActive = !list.active;
    updateMutation.mutate(
      { code: list.code, body: { name: list.name, kind: list.kind, active: nextActive } },
      {
        onSuccess: () => {
          setLifecycleConfirm(false);
          notify('success', nextActive ? t('priceListsPanel.activated') : t('priceListsPanel.deactivated'));
        },
        onError: (error) => notify('error', error.message),
      },
    );
  };

  const currentBindingName = bindPick ? elsewhereByStore.get(bindPick.code) : undefined;
  const confirmBind = () => {
    if (!bindPick) return;
    bindMutation.mutate(
      { code: list.code, storeCode: bindPick.code },
      {
        onSuccess: () => {
          setBindOpen(false);
          setBindPick(null);
          notify('success', t('priceListDetail.bindings.bound', { store: bindPick.name }));
        },
        onError: (error) => notify('error', error.message),
      },
    );
  };

  const confirmUnbind = () => {
    if (!unbindTarget) return;
    unbindMutation.mutate(
      { code: list.code, storeCode: unbindTarget.code },
      {
        onSuccess: () => {
          setUnbindTarget(null);
          notify('success', t('priceListDetail.bindings.unbound', { store: unbindTarget.name }));
        },
        onError: (error) => notify('error', error.message),
      },
    );
  };

  const handleDrawerClose = () => {
    setAddTarget(null);
    setEditTarget(null);
  };
  const drawerState = editTarget ?? (addTarget ? { product: addTarget, lines: addTargetLinesQuery.data ?? [] } : null);

  return (
    <PageContainer
      title={list.name}
      subtitle={
        <Stack direction="row" spacing={1} alignItems="center" component="span">
          <Typography component="span" variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
            {list.code}
          </Typography>
          <Chip
            component="span"
            size="small"
            variant="outlined"
            label={t(PRICE_LIST_KIND_LABELS[list.kind] ?? list.kind)}
          />
        </Stack>
      }
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/catalog/pricing?tab=priceLists')}>
            {t('priceListDetail.backAction')}
          </Button>
          {canPricingAdmin ? (
            <Switch
              checked={list.active}
              onChange={() => setLifecycleConfirm(true)}
              slotProps={{ input: { 'aria-label': t('priceListsPanel.activeToggleAria', { name: list.name }) } }}
            />
          ) : (
            <Tooltip title={t('priceListsPanel.noAdminPermissionTooltip')}>
              <span>
                <Switch
                  checked={list.active}
                  disabled
                  slotProps={{ input: { 'aria-label': t('priceListsPanel.activeToggleAria', { name: list.name }) } }}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip title={canPricingManage ? '' : t('priceListsPanel.noManagePermissionTooltip')}>
            <span>
              <Button
                variant="outlined"
                startIcon={<TrendingDownRoundedIcon />}
                disabled={!canPricingManage}
                onClick={() => navigate(`/catalog/pricing?tab=bulk&targetListCode=${encodeURIComponent(list.code)}`)}
              >
                {t('priceListDetail.repriceIntoListAction')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canPricingManage ? '' : t('priceListsPanel.noManagePermissionTooltip')}>
            <span>
              <IconButton aria-label={t('priceListDetail.editAria')} disabled={!canPricingManage} onClick={() => setEditOpen(true)}>
                <EditRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        {/* Summary */}
        <Alert severity="info" icon={false} sx={{ bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              {t('priceListDetail.summary.lines', { count: linesCount ?? 0 })}
            </Typography>
            <Typography variant="body2">
              {coveragePercent !== null
                ? t('priceListDetail.summary.coverage', { percent: coveragePercent })
                : t('priceListDetail.summary.coverageUnknown')}
            </Typography>
            <Typography variant="body2">
              {t('priceListDetail.summary.stores', { count: bindings.length })}
            </Typography>
          </Stack>
          {nextChangeAt ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('priceListDetail.summary.scheduled', {
                count: scheduled.length,
                date: new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
                  new Date(nextChangeAt),
                ),
              })}
            </Typography>
          ) : null}
        </Alert>

        {/* Stores */}
        <Stack spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('priceListDetail.bindings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('priceListDetail.bindings.hint')}
          </Typography>
          {bindingsQuery.isLoading ? (
            <LoadingState fullHeight={false} />
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {boundStores.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('priceListDetail.bindings.none')}
                </Typography>
              ) : (
                boundStores.map((store) => (
                  <Stack key={store.code} direction="row" spacing={0} alignItems="center">
                    <Chip label={`${store.name} · ${store.code}`} />
                    {canPricingAdmin ? (
                      <IconButton
                        size="small"
                        aria-label={t('priceListDetail.bindings.unbindAria', { name: store.name })}
                        onClick={() => setUnbindTarget(store)}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                ))
              )}
              {canPricingAdmin ? (
                <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setBindOpen(true)}>
                  {t('priceListDetail.bindings.bindAction')}
                </Button>
              ) : (
                <Tooltip title={t('priceListsPanel.noAdminPermissionTooltip')}>
                  <span>
                    <Button size="small" startIcon={<AddRoundedIcon />} disabled>
                      {t('priceListDetail.bindings.bindAction')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>

        {/* List lines */}
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('priceListDetail.lines.title')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 280, flexGrow: 1 }}
              size="small"
              options={searchQuery.data?.content ?? []}
              loading={searchQuery.isFetching}
              inputValue={productSearch}
              onInputChange={(_e, next) => setProductSearch(next)}
              getOptionLabel={(option) => option.name}
              onChange={(_e, value) => {
                if (value) setAddTarget({ id: value.id, name: value.name });
              }}
              disabled={!canPricingManage}
              renderInput={(params) => (
                <TextField {...params} label={t('priceListDetail.lines.searchLabel')} />
              )}
            />
            <ToggleButtonGroup
              value={linesFilter}
              exclusive
              size="small"
              onChange={(_e, next: LinesFilter | null) => {
                if (next) {
                  setLinesFilter(next);
                  setPage(0);
                }
              }}
            >
              <ToggleButton value="all">{t('priceListDetail.lines.filterAll')}</ToggleButton>
              <ToggleButton value="scheduled">{t('priceListDetail.lines.filterScheduled')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {linesQuery.isLoading ? (
            <LoadingState label={t('priceListDetail.lines.loading')} />
          ) : linesQuery.isError ? (
            <ErrorState message={t('priceListDetail.lines.loadError')} onRetry={() => void linesQuery.refetch()} />
          ) : filteredLines.length === 0 ? (
            <EmptyState title={t('priceListDetail.lines.empty.title')} description={t('priceListDetail.lines.empty.description')} />
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('priceListDetail.lines.table.product')}</TableCell>
                      {tiers.map((tier) => (
                        <TableCell key={tier} align="right">
                          {t('priceGridPanel.table.tierFrom', { qty: tier })}
                        </TableCell>
                      ))}
                      <TableCell align="right">{t('priceListDetail.lines.table.base')}</TableCell>
                      <TableCell>{t('priceListDetail.lines.table.status')}</TableCell>
                      <TableCell align="right">{t('priceListsPanel.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageLines.map((line) => (
                      <PriceListLineRow
                        key={line.id}
                        line={line}
                        tiers={tiers}
                        canManage={canPricingManage}
                        onEdit={(product, lines) => setEditTarget({ product, lines })}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <CompactTablePagination
                count={filteredLines.length}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_e, next) => setPage(next)}
                onRowsPerPageChange={(e) => {
                  setSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Stack>
      </Stack>

      <PriceListFormDialog open={editOpen} priceList={list} pending={updateMutation.isPending} onClose={() => setEditOpen(false)} onSave={handleSaveEdit} />

      <Dialog open={lifecycleConfirm} onClose={updateMutation.isPending ? undefined : () => setLifecycleConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {list.active ? t('priceListsPanel.deactivateConfirmTitle') : t('priceListsPanel.activateConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {list.active
              ? t('priceListsPanel.deactivateConfirmBody', { name: list.name, count: bindings.length })
              : t('priceListsPanel.activateConfirmBody', { count: bindings.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleConfirm(false)} disabled={updateMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color={list.active ? 'error' : 'primary'}
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={confirmLifecycle}
          >
            {list.active ? t('priceListsPanel.deactivateAction') : t('priceListsPanel.activateAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bindOpen} onClose={bindMutation.isPending ? undefined : () => setBindOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('priceListDetail.bindings.bindAction')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={bindableStores}
              getOptionLabel={(option) =>
                `${option.name} · ${option.code}${elsewhereByStore.has(option.code) ? ` — ${t('priceListDetail.bindings.boundElsewhere', { list: elsewhereByStore.get(option.code) })}` : ''}`
              }
              value={bindPick}
              onChange={(_e, value) => setBindPick(value)}
              renderInput={(params) => <TextField {...params} label={t('priceListDetail.bindings.storeLabel')} autoFocus />}
            />
            {currentBindingName ? (
              <Alert severity="warning">
                {t('priceListDetail.bindings.bindReplaceNote', { current: currentBindingName })}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBindOpen(false)} disabled={bindMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!bindPick || bindMutation.isPending}
            startIcon={bindMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={confirmBind}
          >
            {t('priceListDetail.bindings.bindAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unbindTarget !== null} onClose={unbindMutation.isPending ? undefined : () => setUnbindTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('priceListDetail.bindings.unbindConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('priceListDetail.bindings.unbindConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnbindTarget(null)} disabled={unbindMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={unbindMutation.isPending}
            startIcon={unbindMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={confirmUnbind}
          >
            {t('priceListDetail.bindings.unbindAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <PriceCellEditorDrawer
        open={drawerState !== null}
        product={drawerState?.product ?? { id: '' }}
        context={{ type: 'list', code: list.code, name: list.name }}
        existingLines={drawerState?.lines ?? []}
        onClose={handleDrawerClose}
        onResult={(severity, message) => notify(severity, message)}
      />

      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={clear} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
